import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/AuthContext";
import { LanguageProvider } from "@/lib/LanguageContext";
import { HelmetProvider } from "react-helmet-async";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminPWAManager from "@/components/AdminPWAManager";

// Client Pages (English) - PUBLIC
import HomePage from "@/pages/HomePage";
import GetQuotePage from "@/pages/GetQuotePage";
import QuoteSubmittedPage from "@/pages/QuoteSubmittedPage";
import ViewQuotePage from "@/pages/ViewQuotePage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import ACIPaymentPage from "@/pages/ACIPaymentPage";
import BlogPage from "@/pages/BlogPage";

// Admin Pages (Romanian)
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminChangePasswordPage from "@/pages/admin/AdminChangePasswordPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminRequestDetail from "@/pages/admin/AdminRequestDetail";
import UserManagementPage from "@/pages/admin/UserManagementPage";
import ActivityLogsPage from "@/pages/admin/ActivityLogsPage";
import SecuritySettingsPage from "@/pages/admin/SecuritySettingsPage";
import DocumentationPage from "@/pages/admin/DocumentationPage";
import BulkInvoicesPage from "@/pages/admin/BulkInvoicesPage";
import BlogManagementPage from "@/pages/admin/BlogManagementPage";
import TermsAndConditions from "@/pages/legal/TermsAndConditions";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import WebsiteDisclaimer from "@/pages/legal/WebsiteDisclaimer";
import CookieConsentBanner from "@/components/CookieConsentBanner";

function App() {
  return (
    <HelmetProvider>
    <div className="min-h-screen bg-[#020617]">
      <AuthProvider>
        <LanguageProvider>
        <BrowserRouter>
          <AdminPWAManager />
          <CookieConsentBanner />
          <Routes>
          {/* ========================================
              PUBLIC CLIENT ROUTES (English)
              No authentication required
          ======================================== */}
          <Route path="/" element={<HomePage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/get-quote" element={<GetQuotePage />} />
          <Route path="/quote-submitted/:quoteId" element={<QuoteSubmittedPage />} />
          <Route path="/quote/:quoteId" element={<ViewQuotePage />} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/aci/:invoiceId" element={<ACIPaymentPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/website-disclaimer" element={<WebsiteDisclaimer />} />
          
          {/* ========================================
              ADMIN ROUTES (Romanian)
              Authentication required
          ======================================== */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route
            path="/admin/change-password"
            element={
              <ProtectedRoute allowPasswordChange={true}>
                <AdminChangePasswordPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/request/:quoteId"
            element={
              <ProtectedRoute>
                <AdminRequestDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requireOwner={true}>
                <UserManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/activity-logs"
            element={
              <ProtectedRoute requireOwner={true}>
                <ActivityLogsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/security"
            element={
              <ProtectedRoute>
                <SecuritySettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/documentation"
            element={
              <ProtectedRoute requireOwner={true}>
                <DocumentationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/bulk-invoices"
            element={
              <ProtectedRoute requireOwner={true}>
                <BulkInvoicesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/blog"
            element={
              <ProtectedRoute requireOwner={true}>
                <BlogManagementPage />
              </ProtectedRoute>
            }
          />
          
          {/* Redirect unknown routes */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#0f172a',
              color: '#f8fafc',
              border: '1px solid #1e293b',
            },
          }}
        />
        </LanguageProvider>
      </AuthProvider>
    </div>
    </HelmetProvider>
  );
}

export default App;
